<template>
    <span v-if="prescriberId" class="npi-validation-badge" @click="cycleTestScenario" :style="isTestable ? 'cursor: pointer;' : ''">
        <span v-if="loading" class="spinner-border spinner-border-sm text-muted" role="status" />
        <template v-else-if="result">
            <span v-if="result.isValid" class="badge badge-success" v-b-tooltip.hover title="NPI is active and valid">
                <i class="fa fa-check-circle" /> NPI Valid
            </span>
            <span v-else-if="result.isNotFound" class="badge badge-warning" v-b-tooltip.hover title="NPI not found in the provider registry">
                <i class="fa fa-search" /> NPI Not Found
            </span>
            <span v-else class="badge badge-danger" v-b-tooltip.hover :title="validationTooltip">
                <i class="fa fa-exclamation-triangle" /> NPI Issue
            </span>
        </template>
        <span v-else-if="error" class="badge badge-secondary" v-b-tooltip.hover title="Unable to validate NPI">
            <i class="fa fa-question-circle" /> NPI Unknown
        </span>
        <small v-if="isTestable && testScenarioIndex >= 0" class="text-muted ml-1">(test: {{ testScenarios[testScenarioIndex] }})</small>
    </span>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import axios from "axios";
import { isNonProduction } from "@/util/environment";

interface NpiValidationResult {
    npiNumber: string | null;
    providerName: string | null;
    isValid: boolean;
    isNPIExpired: boolean;
    isNPIInactive: boolean;
    isDeceased: boolean;
    isCurrentlySanctioned: boolean;
    isInactivated: boolean;
    isNotFound: boolean;
    npiStatus: string | null;
    npiExpirationDate: string | null;
    hcpStatusDescription: string | null;
    primarySpecialty: string | null;
    validationMessages: string[];
    lastValidated: string | null;
}

@Component
export default class NpiValidationBadge extends Vue {
    @Prop({ required: true }) prescriberId!: number;

    result: NpiValidationResult | null = null;
    loading = false;
    error = false;

    testScenarioIndex = -1;
    testScenarios = ["valid", "expired", "inactive", "deceased", "sanctioned", "inactivated", "multiple", "not-found"];

    get isTestable(): boolean {
        return isNonProduction(this.$environment);
    }

    get validationTooltip(): string {
        if (!this.result?.validationMessages?.length) return "NPI validation failed";
        return this.result.validationMessages.join("; ");
    }

    @Watch("prescriberId", { immediate: true })
    async onPrescriberIdChanged(newId: number) {
        if (!newId) return;
        this.testScenarioIndex = -1;
        await this.validate();
    }

    async validate() {
        this.loading = true;
        this.error = false;
        this.result = null;
        try {
            const response = await axios.get(`/api/Prescriber/${this.prescriberId}/npi-validation`);
            if (response.data?.enabled === false) {
                this.result = null;
                return;
            }
            this.result = response.data;
        } catch {
            this.error = true;
        } finally {
            this.loading = false;
        }
    }

    async cycleTestScenario() {
        if (!this.isTestable) return;
        this.testScenarioIndex = (this.testScenarioIndex + 1) % this.testScenarios.length;
        const scenario = this.testScenarios[this.testScenarioIndex];

        this.loading = true;
        this.error = false;
        this.result = null;
        try {
            const response = await axios.get(`/api/Prescriber/test/${scenario}`);
            this.result = response.data?.validationStatus ?? response.data;
        } catch {
            this.error = true;
        } finally {
            this.loading = false;
        }
    }
}
</script>

<style scoped>
.npi-validation-badge {
    display: inline-block;
    margin-left: 8px;
    vertical-align: middle;
}
</style>
